import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useImpostazioniMarketing } from '../hooks/useImpostazioniMarketing';
import DocumentHead from '../components/DocumentHead';
import clienteAuthService from '../api/authService';

// ─── Componente percento badge ─────────────────────────────────────────────
function PercBadge({ value, label, loading, suffix = '%' }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-5xl md:text-6xl font-black text-white drop-shadow-md tabular-nums">
        {loading ? (
          <span className="inline-block w-16 h-12 bg-white/20 rounded-lg animate-pulse" />
        ) : (
          `${value}${suffix}`
        )}
      </div>
      <div className="text-sm md:text-base text-white/90 font-medium text-center max-w-[160px] leading-tight">{label}</div>
    </div>
  );
}

// ─── Step del flusso ─────────────────────────────────────────────────────
const FLOW_STEPS = [
  { emoji: '🔗', title: 'Condividi il tuo link', desc: 'Dal tuo wallet trovi il codice amico. Lo mandi a chi vuoi — amici, parenti, colleghi, gruppi social.' },
  { emoji: '🛒', title: 'Loro acquistano', desc: 'Si registrano su Mangitalia usando il tuo codice e fanno il loro primo ordine.' },
  { emoji: '💰', title: 'Tu guadagni', desc: 'Il credito appare automaticamente nel tuo wallet non appena il loro ordine viene consegnato.' },
  { emoji: '🏦', title: 'Spendi o preleva', desc: 'Usi i crediti come sconto sugli ordini. Oppure, da Closer, li prelevi direttamente in banca.' },
];

// ─── Requisiti Closer ─────────────────────────────────────────────────────
const CLOSER_REQUISITI = [
  { icon: '🪪', req: 'Codice Fiscale valido', note: 'Per identificazione fiscale' },
  { icon: '🏦', req: 'IBAN italiano intestato a te', note: 'Per ricevere i bonifici' },
  { icon: '📝', req: 'Nominativo intestatario', note: 'Deve corrispondere al titolare del conto' },
  { icon: '🔐', req: 'Password account', note: 'Per confermare l\'operazione (irreversibile)' },
];

// ─── FAQ ─────────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: 'Devo fare qualcosa di speciale per iniziare?',
    a: 'No. Basta avere un account Mangitalia: il tuo codice amico è già generato e disponibile nella sezione "Wallet & Inviti" della tua area riservata. Puoi iniziare a condividerlo subito.',
  },
  {
    q: 'Quando ricevo il credito?',
    a: 'Per i clienti invitati: quando il loro primo ordine viene segnato come consegnato. Per i produttori invitati: ogni volta che viene accreditato un pagamento a loro, durante i primi mesi dalla loro registrazione.',
  },
  {
    q: 'I crediti scadono?',
    a: 'No. Il credito nel tuo wallet non scade. Puoi usarlo quando vuoi, scalandolo automaticamente dai tuoi ordini al momento del checkout.',
  },
  {
    q: 'Cosa cambia se divento Closer?',
    a: 'Come cliente normale puoi usare i crediti per scontarti gli ordini. Da Closer puoi anche richiedere un bonifico diretto sul tuo conto corrente, una volta raggiunta la soglia minima. Le regole di guadagno sono identiche in entrambi i casi.',
  },
  {
    q: 'La conversione a Closer è davvero irreversibile?',
    a: 'Sì. Una volta diventato Closer non si torna indietro alla tipologia "cliente normale". Non perdi nessuna funzione: puoi continuare ad acquistare, a condividere il codice e a usare i crediti negli ordini. L\'unica differenza è che hai in più la possibilità di richiedere i bonifici.',
  },
  {
    q: 'Posso invitare sia clienti che produttori?',
    a: 'Sì, lo stesso codice funziona per entrambi. Quando un cliente si registra con il tuo codice, guadagni sul suo primo ordine. Quando un produttore si registra con il tuo codice, guadagni su tutto il suo fatturato per i mesi stabiliti.',
  },
  {
    q: 'Devo emettere fattura per i guadagni?',
    a: 'Se sei un Closer e ricevi bonifici, valuta con il tuo commercialista come trattare fiscalmente i guadagni, in base alla tua situazione personale. Mangitalia non è un datore di lavoro: sei tu a gestire la tua posizione fiscale.',
  },
  {
    q: 'Quante persone posso invitare?',
    a: 'Non c\'è limite. Il programma funziona per ogni persona che usi il tuo codice — senza tetto massimo al numero di inviti.',
  },
];

export default function DiventaCloser() {
  const navigate = useNavigate();
  const { impostazioni, loading } = useImpostazioniMarketing();
  const [openFaq, setOpenFaq] = useState(null);
  const isAuth = clienteAuthService.isAuthenticated?.() || false;

  const perc1 = impostazioni.referral_perc_primo_ordine;
  const perc2 = impostazioni.referral_perc_rolling_produttore;
  const mesi = impostazioni.referral_mesi_rolling_produttore;
  const soglia = impostazioni.closer_soglia_prelievo_minimo;

  return (
    <div className="min-h-screen">
      <DocumentHead
        title="Guadagna con Mangitalia — Programma Inviti e Closer"
        description={`Invita amici e produttori su Mangitalia e guadagna crediti sul loro primo ordine o sul venduto. Diventa Closer e preleva i guadagni in bonifico.`}
        path="/diventa-closer"
      />

      {/* ─── Hero ───────────────────────────────────────────────────────── */}
      <section className="mang-gradient text-white px-4 pt-24 pb-16 md:pt-32 md:pb-24 relative overflow-hidden -mt-14">

        {/* Sfondo decorativo */}
        <div className="absolute inset-0 opacity-10 pointer-events-none select-none">
          <div className="absolute top-8 left-8 text-[120px]">🎁</div>
          <div className="absolute bottom-8 right-12 text-[90px]">🇮🇹</div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-white/20 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
              Programma Community Mangitalia
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl md:text-6xl font-black leading-tight mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Porta qualcuno su Mangitalia.
            <br />
            <span className="font-sacramento text-5xl md:text-7xl font-normal">
              Guadagna insieme a lui.
            </span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Non è marketing. Non è un lavoro. È semplicemente condividere qualcosa di buono
            con chi ti sta vicino — e venire premiato per averlo fatto.
          </motion.p>

          {/* Percentuali dinamiche */}
          <motion.div
            className="flex flex-wrap justify-center gap-8 md:gap-16 mb-10"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <PercBadge
              value={perc1}
              label="sul primo ordine del cliente invitato"
              loading={loading}
            />
            <PercBadge
              value={perc2}
              label={`sul venduto del produttore invitato (${mesi} mesi)`}
              loading={loading}
            />
          </motion.div>

          <motion.div
            className="flex flex-wrap justify-center gap-3"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {isAuth ? (
              <Link
                to="/area-riservata?tab=wallet"
                className="bg-white text-[#c78244] px-8 py-3.5 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-shadow"
              >
                Vai al mio Wallet →
              </Link>
            ) : (
              <>
                <Link
                  to="/area-riservata/registrati"
                  className="bg-white text-[#c78244] px-8 py-3.5 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-shadow"
                >
                  Inizia gratis →
                </Link>
                <Link
                  to="/area-riservata/login"
                  className="border border-white/60 text-white px-8 py-3.5 rounded-full font-semibold text-lg hover:bg-white/10 transition-colors"
                >
                  Accedi
                </Link>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* ─── Come funziona ──────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-[#c78244] mb-3">Come funziona</h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Dal tuo codice al credito in wallet, in quattro passi semplici.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {FLOW_STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              className="bg-white rounded-2xl border border-[#f3deb8] p-5 relative"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="absolute -top-3 -left-2 bg-[#c78244] text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow">
                {i + 1}
              </div>
              <div className="text-4xl mb-3">{step.emoji}</div>
              <h3 className="font-bold text-gray-800 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── Due livelli ────────────────────────────────────────────────── */}
      <section className="bg-[#fff8ea] border-y border-[#f3deb8] py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-[#c78244] mb-3">Il programma ha due livelli</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Chiunque può guadagnare crediti. Chi vuole fare di più può diventare Closer.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Livello 1 */}
            <motion.div
              className="bg-white rounded-2xl border-2 border-[#f3deb8] p-7"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🌱</span>
                <div>
                  <div className="font-bold text-lg text-gray-800">Membro della Community</div>
                  <div className="text-xs text-[#c78244] font-semibold uppercase tracking-wide">Tutti i clienti registrati</div>
                </div>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                Dal momento in cui ti registri, hai già un codice amico personale.
                Condividilo e guadagna automaticamente ogni volta che chi hai invitato acquista o vende
                su Mangitalia — senza nessun impegno aggiuntivo.
              </p>

              <ul className="space-y-3 mb-6">
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#F6CF48] font-bold shrink-0 mt-0.5">✓</span>
                  <span>
                    <b>{loading ? '...' : perc1}%</b> del primo ordine completato del cliente invitato
                  </span>
                </li>
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#F6CF48] font-bold shrink-0 mt-0.5">✓</span>
                  <span>
                    <b>{loading ? '...' : perc2}%</b> su tutto il venduto del produttore invitato
                    per {loading ? '...' : mesi} mesi
                  </span>
                </li>
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#F6CF48] font-bold shrink-0 mt-0.5">✓</span>
                  <span>Crediti usabili come sconto automatico al checkout</span>
                </li>
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#F6CF48] font-bold shrink-0 mt-0.5">✓</span>
                  <span>Nessun obbligo, nessuna scadenza, nessun tetto agli inviti</span>
                </li>
              </ul>

              <div className="bg-[#fff8ea] rounded-xl p-3 text-xs text-[#7a4a1f]">
                ✅ Attivo automaticamente — non serve fare nulla di speciale.
              </div>
            </motion.div>

            {/* Livello 2 */}
            <motion.div
              className="bg-white rounded-2xl border-2 border-[#c78244] p-7 relative overflow-hidden"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-4 right-4">
                <span className="bg-[#c78244] text-white text-xs font-bold px-3 py-1 rounded-full">Opzionale</span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🏆</span>
                <div>
                  <div className="font-bold text-lg text-gray-800">Closer Mangitalia</div>
                  <div className="text-xs text-[#c78244] font-semibold uppercase tracking-wide">Chi vuole andare oltre</div>
                </div>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                Hai accumulato crediti e vuoi vederli arrivare sul conto in banca? Il ruolo Closer
                è pensato per chi partecipa attivamente alla crescita della community e vuole
                trasformare questa partecipazione in un guadagno concreto e tracciabile.
              </p>

              <ul className="space-y-3 mb-6">
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#c78244] font-bold shrink-0 mt-0.5">✓</span>
                  <span>Tutto ciò che hai come membro normale, più…</span>
                </li>
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#c78244] font-bold shrink-0 mt-0.5">✓</span>
                  <span>
                    <b>Prelievo via bonifico</b> dal wallet (soglia minima:{' '}
                    {loading ? '...' : `${soglia}€`})
                  </span>
                </li>
                <li className="flex gap-3 text-sm text-gray-700">
                  <span className="text-[#c78244] font-bold shrink-0 mt-0.5">✓</span>
                  <span>Storico prelievi tracciato nel tuo profilo</span>
                </li>
              </ul>

              <div className="bg-orange-50 border-l-4 border-orange-300 rounded-r-xl px-4 py-3 text-xs text-orange-800 mb-5">
                <b>⚠️ Nota bene:</b> La conversione a Closer è irreversibile. Continui ad acquistare e
                usare il sito esattamente come prima — l'unica aggiunta è la possibilità di richiedere bonifici.
              </div>

              {isAuth ? (
                <Link
                  to="/area-riservata?tab=wallet"
                  className="block text-center bg-[#c78244] text-white px-5 py-3 rounded-xl font-bold hover:bg-[#a66a35] transition-colors"
                >
                  Diventa Closer dal tuo account →
                </Link>
              ) : (
                <Link
                  to="/area-riservata/login"
                  className="block text-center bg-[#c78244] text-white px-5 py-3 rounded-xl font-bold hover:bg-[#a66a35] transition-colors"
                >
                  Accedi per diventare Closer →
                </Link>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Requisiti Closer ───────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-[#c78244] mb-3">Cosa serve per diventare Closer</h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Quattro dati, un'operazione irreversibile, la libertà di prelevare quando vuoi.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {CLOSER_REQUISITI.map((item, i) => (
            <motion.div
              key={item.req}
              className="flex gap-4 bg-white rounded-2xl border border-[#f3deb8] p-5"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <span className="text-3xl shrink-0">{item.icon}</span>
              <div>
                <div className="font-semibold text-gray-800 text-sm">{item.req}</div>
                <div className="text-xs text-gray-500 mt-0.5">{item.note}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-[#fff8ea] border border-[#f3deb8] rounded-2xl p-6 text-sm text-[#7a4a1f] leading-relaxed"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="mb-2">
            <strong>Informativa trasparenza:</strong> Diventare Closer non crea un rapporto di lavoro
            subordinato con Mangitalia. I guadagni derivano da un programma di referral volontario.
            Ogni Closer è responsabile della propria gestione fiscale dei guadagni ricevuti.
          </p>
          <p>
            I dati fiscali (CF, IBAN) sono trattati secondo la{' '}
            <Link to="/privacy" className="underline">Privacy Policy</Link> di Mangitalia
            e utilizzati esclusivamente per le operazioni di pagamento.
          </p>
        </motion.div>
      </section>

      {/* ─── Cosa guadagni — schema visivo ─────────────────────────────── */}
      <section className="bg-[#fff8ea] border-y border-[#f3deb8] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-[#c78244] mb-3">Schema del guadagno</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Inviti Clienti */}
            <motion.div
              className="bg-white rounded-2xl border border-[#f3deb8] p-6"
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-2xl mb-3">👥 Inviti Clienti</div>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex gap-2 items-start">
                  <span className="bg-[#F6CF48]/30 text-[#7a4a1f] font-bold text-xs px-2 py-0.5 rounded shrink-0">Quando</span>
                  <span>Il cliente invitato completa il suo <b>primo ordine</b> consegnato</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="bg-[#F6CF48]/30 text-[#7a4a1f] font-bold text-xs px-2 py-0.5 rounded shrink-0">Quanto</span>
                  <span>
                    {loading ? '…' : <b>{perc1}% del valore del suo carrello</b>} entra nel tuo wallet
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="bg-[#F6CF48]/30 text-[#7a4a1f] font-bold text-xs px-2 py-0.5 rounded shrink-0">Volte</span>
                  <span>Solo una volta (il primo ordine)</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-[#fff8ea] rounded-xl text-xs text-[#7a4a1f]">
                💡 Esempio: inviti Mario, che ordina prodotti per 50€.
                Tu ricevi {loading ? '…' : `${perc1 / 2}€`} di credito wallet.
              </div>
            </motion.div>

            {/* Inviti Produttori */}
            <motion.div
              className="bg-white rounded-2xl border border-[#f3deb8] p-6"
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-2xl mb-3">🧀 Inviti Produttori</div>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex gap-2 items-start">
                  <span className="bg-[#F6CF48]/30 text-[#7a4a1f] font-bold text-xs px-2 py-0.5 rounded shrink-0">Quando</span>
                  <span>Ogni volta che il produttore invitato riceve un pagamento</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="bg-[#F6CF48]/30 text-[#7a4a1f] font-bold text-xs px-2 py-0.5 rounded shrink-0">Quanto</span>
                  <span>
                    {loading ? '…' : <b>{perc2}% del suo accredito netto</b>} entra nel tuo wallet
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="bg-[#F6CF48]/30 text-[#7a4a1f] font-bold text-xs px-2 py-0.5 rounded shrink-0">Per quanto</span>
                  <span>Per {loading ? '…' : mesi} mesi dalla sua registrazione</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-[#fff8ea] rounded-xl text-xs text-[#7a4a1f]">
                💡 Esempio: inviti il Caseificio Rossi, che vende 2.000€ in {loading ? '…' : mesi} mesi.
                Tu ricevi {loading ? '…' : `${(perc2 / 100) * 2000}€`} di credito in totale.
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-[#c78244] mb-3">Domande frequenti</h2>
        </motion.div>

        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <motion.div
              key={item.q}
              className="bg-white rounded-xl border border-[#f3deb8] overflow-hidden"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
            >
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left px-5 py-4 font-semibold text-gray-800 flex justify-between items-center gap-4 hover:bg-[#fff8ea] transition-colors"
              >
                <span className="text-sm">{item.q}</span>
                <span className="text-[#c78244] text-xl shrink-0 transition-transform" style={{ transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
              </button>
              <AnimatePresence initial={false}>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{item.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── CTA finale ─────────────────────────────────────────────────── */}
      <motion.section
        className="mang-gradient text-white text-center py-16 px-6"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Il tuo codice amico ti aspetta
        </h2>
        <p className="text-lg text-white/85 max-w-xl mx-auto mb-8">
          Accedi o registrati: trovi il tuo codice personale subito nel wallet.
          Copialo e inizia a condividerlo.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          {isAuth ? (
            <Link
              to="/area-riservata?tab=wallet"
              className="bg-white text-[#c78244] px-8 py-3.5 rounded-full font-bold text-lg shadow-xl"
            >
              Vai al mio Wallet →
            </Link>
          ) : (
            <>
              <Link
                to="/area-riservata/registrati"
                className="bg-white text-[#c78244] px-8 py-3.5 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl"
              >
                Registrati gratis →
              </Link>
              <Link
                to="/area-riservata/login"
                className="border border-white/60 text-white px-8 py-3.5 rounded-full font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                Ho già un account
              </Link>
            </>
          )}
        </div>
      </motion.section>

      {/* ─── Footer minimale ────────────────────────────────────────────── */}
      <footer className="text-center py-8 text-gray-400 text-sm border-t border-[#f3deb8]">
        <div className="flex flex-wrap justify-center gap-4 mb-2">
          <Link to="/" className="hover:text-[#c78244]">Home</Link>
          <Link to="/catalogo" className="hover:text-[#c78244]">Catalogo</Link>
          <Link to="/diventa-partner" className="hover:text-[#c78244]">Vendi su Mangitalia</Link>
          <Link to="/per-partner" className="hover:text-[#c78244]">Modello business</Link>
          <Link to="/termini" className="hover:text-[#c78244]">Termini</Link>
          <Link to="/privacy" className="hover:text-[#c78244]">Privacy</Link>
        </div>
        <p>© {new Date().getFullYear()} Mangitalia — eccellenze alimentari italiane</p>
      </footer>
    </div>
  );
}
